import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router, NavigationEnd } from '@angular/router';
import { QrScannerComponent } from '../qr-scanner/qr-scanner.component';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-qr-scanner-button',
  standalone: true,
  imports: [CommonModule, TranslateModule, QrScannerComponent],
  template: `
    <!-- Floating QR Scanner Button -->
    <button 
      class="qr-scanner-btn"
      (click)="openScanner()"
      [attr.aria-label]="'QR.SCAN_BUTTON' | translate"
      [class.hidden]="!isVisible">
      <i class="fas fa-qrcode"></i>
    </button>

    <!-- QR Scanner Modal -->
    @if (showScanner) {
      <app-qr-scanner
        (closed)="closeScanner()"
        (scanned)="onScanned($event)">
      </app-qr-scanner>
    }
  `,
  styles: [`
    .qr-scanner-btn {
      position: fixed;
      left: 30px;
      bottom: 30px;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: linear-gradient(135deg, #9e1c20 0%, #c62828 100%);
      border: none;
      box-shadow: 0 4px 12px rgba(158, 28, 32, 0.4);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 24px;
      z-index: 9999;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 1;
      transform: scale(1);
    }

    .qr-scanner-btn:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 20px rgba(158, 28, 32, 0.5);
    }

    .qr-scanner-btn:active {
      transform: scale(0.95);
    }

    .qr-scanner-btn.hidden {
      opacity: 0;
      transform: scale(0);
      pointer-events: none;
    }

    .qr-scanner-btn i {
      animation: pulse 2s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
    }

    /* Mobile responsive - avoid navigation collision */
    @media (max-width: 600px) {
      .qr-scanner-btn {
        left: 16px;
        bottom: 80px;
        width: 52px;
        height: 52px;
        font-size: 22px;
      }
    }

    /* Tablet */
    @media (min-width: 601px) and (max-width: 1024px) {
      .qr-scanner-btn {
        left: 24px;
        bottom: 60px;
      }
    }

    /* iOS safe-area support */
    @supports (padding: max(0px)) {
      @media (max-width: 600px) {
        .qr-scanner-btn {
          bottom: max(80px, calc(80px + env(safe-area-inset-bottom)));
        }
      }
    }
  `]
})
export class QrScannerButtonComponent implements OnInit, OnDestroy {
  showScanner = false;
  isVisible = true;
  private routerSubscription?: Subscription;

  constructor(private router: Router) {}

  ngOnInit() {
    // Hide QR button on product detail page (Sell button takes priority there)
    this.checkRouteVisibility(this.router.url);

    // Listen to route changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.checkRouteVisibility(event.url);
      });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  private checkRouteVisibility(url: string) {
    // Hide QR button on product detail page
    const isProductDetailPage = url.includes('/I4UProductPlanDetail/');
    this.isVisible = !isProductDetailPage;
  }

  openScanner() {
    this.showScanner = true;
  }

  closeScanner() {
    this.showScanner = false;
  }

  onScanned(applicationNo: string) {
    console.log('Scanned application:', applicationNo);
    this.closeScanner();
  }
}
